/**
 * App_inAppFeedback.ts
 *
 * The entry point for inAppFeedback.
 */

import * as App_Common from "./App_Common";
import * as Configuration from "./Configuration/Configuration";
import * as Logging from "./Logging/Logging";
import * as UI from "./UI/UI";
import * as Window from "./Window/Window";
import { ILaunchOptionsInAppFeedback } from "./Configuration/ILaunchOptions";

import { Promise } from "es6-promise";

/**
 * Has inAppFeedback been initialized
 */
let initialized: boolean = false;

/**
 * Initialize
 * @returns A promise which will be rejected if the call fails.
 */
export function initialize(): Promise<any> {
	return new Promise((resolve, reject) => {
		if (!initialized) {
			App_Common.initialize()
			.then(
				function onInitializeFulfilled() {
					if (!Window.get().OfficeBrowserFeedback.initOptions) {
						reject("Window.OfficeBrowserFeedback.initOptions not set");
						return;
					}

					Configuration.get().setInAppFeedbackInitOptions(Window.get().OfficeBrowserFeedback.initOptions);
					initialized = true;

					resolve();
				}
			)
			.catch(
				function onInitializeRejected(err) { reject("Initialization failed: {" + err + "}"); }
			);
		} else {
			resolve();
		}
	});
}

/**
 * Has the control been opened. Prevents more than one dialog being generated.
 */
let opened: boolean = false;

/**
 * Handler to launch the multi feedback dialog
 * @param launchOptions optional feedback properties
 * @returns A promise which will be rejected if the call fails.
 */
export function multiFeedback(launchOptions?: ILaunchOptionsInAppFeedback): Promise<any> {
	return new Promise((resolve, reject) => {
		if (opened) {
			reject("Control already open");
			return;
		}

		initialize()
		.then(
			function onInitializeFulfilled() {
				Configuration.get().setInAppFeedbackLaunchOptions(launchOptions);
				UI.createBellyBandMulti(function () { opened = false; });
				Logging.getLogger().logEvent(Logging.EventIds.InApp.UI.Picker.Shown.VALUE,
					{
						IsBugEnabled: Configuration.get().getInAppFeedbackInitOptions().bugForm,
						IsIdeaEnabled: Configuration.get().getInAppFeedbackInitOptions().userVoice !== undefined,
					});
				opened = true;
				resolve();
			}
		)
		.catch(
			function onInitializeRejected(err) { reject("Initialization failed: {" + err + "}"); }
		);
	});
}

/**
 * Handler to launch the single feedback dialog
 * @param feedbackType the feedback type
 * @param launchOptions optional feedback properties
 * @returns A promise which will be rejected if the call fails.
 */
export function singleFeedback(feedbackType: string, launchOptions?: ILaunchOptionsInAppFeedback): Promise<any> {
	return new Promise((resolve, reject) => {
		if (opened) {
			reject("Control already open");
			return;
		}

		let feedbackTypeEnum = Configuration.validateFeedbackType(feedbackType);

		initialize()
		.then(
			function onInitializeFulfilled() {
				Configuration.get().setInAppFeedbackLaunchOptions(launchOptions);
				UI.createBellyBandSingle(function () { opened = false; }, feedbackTypeEnum);
				Logging.getLogger().logEvent(Logging.EventIds.InApp.UI.Form.Shown.VALUE,
					{
						FeedbackType: feedbackTypeEnum,
					});
				opened = true;
				resolve();
			}
		)
		.catch(
			function onInitializeRejected(err) { reject("Initialization failed: {" + err + "}"); }
		);
	});
}

/**
 * Reset the inappfeedback module. Used in unit tests.
 */
export function reset() {
	App_Common.reset();
	initialized = false;
	opened = false;
}

/* Make the FeedbackDialog methods available globally */
Window.setMultiFeedback(multiFeedback);
Window.setSingleFeedback(singleFeedback);
